import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, User, LogOut } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

function NavLink({ href, children }) {
  const location = useLocation();
  const active = location.pathname === href;
  
  return (
    <Link
      to={href}
      data-testid={`nav-link-${href.replace('/', '') || 'home'}`}
      className={`text-sm font-medium transition-colors duration-200 ${
        active ? 'text-white' : 'text-white/80 hover:text-white'
      }`}
    >
      {children}
    </Link>
  );
}

export default function Nav() {
  const { user, isAuthenticated, logout } = useAuth();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'nav-glass shadow-lg' : 'bg-navy'
      }`}
      data-testid="main-nav"
    >
      <div className="container-custom py-4 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
          <div className="h-10 w-10 rounded-xl bg-copper flex items-center justify-center text-white font-heading font-bold text-xl transition-transform duration-200 group-hover:scale-105">
            R
          </div>
          <div className="leading-tight">
            <div className="text-white font-heading font-semibold text-lg tracking-tight">BevRelay</div>
            <div className="text-white/60 text-xs font-body">Beverage Relationship Network</div>
          </div>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-8">
          <NavLink href="/suppliers">For Suppliers</NavLink>
          <NavLink href="/buyers">For Buyers</NavLink>
          <NavLink href="/build-brand">Build Brand</NavLink>
          <NavLink href="/ai-blueprint">AI Blueprint</NavLink>
          <NavLink href="/trade-pay">Trade Pay</NavLink>
          <NavLink href="/distributor-reps">Distributor Reps</NavLink>
          <NavLink href="/target-list">Target List</NavLink>
          <NavLink href="/about">About</NavLink>
          
          {isAuthenticated ? (
            <div className="flex items-center gap-4 ml-2">
              <Link
                to="/dashboard"
                data-testid="nav-dashboard-btn"
                className="inline-flex items-center gap-2 rounded-xl bg-copper px-5 py-2.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-copper-dark hover:scale-105"
              >
                <User className="w-4 h-4" />
                Dashboard
              </Link>
            </div>
          ) : (
            <div className="flex items-center gap-3 ml-2">
              <Link
                to="/login"
                data-testid="nav-login-btn"
                className="text-sm font-medium text-white/80 hover:text-white transition-colors"
              >
                Sign In
              </Link>
              <Link
                to="/register"
                data-testid="nav-register-btn"
                className="inline-flex items-center rounded-xl bg-copper px-5 py-2.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-copper-dark hover:scale-105"
              >
                Get Started
              </Link>
            </div>
          )}
        </nav>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="md:hidden p-2 text-white"
          data-testid="mobile-menu-btn"
          aria-label="Toggle menu"
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-navy border-t border-white/10" data-testid="mobile-menu">
          <nav className="container-custom py-4 flex flex-col gap-4">
            <Link 
              to="/suppliers" 
              onClick={() => setMobileMenuOpen(false)}
              className="text-white/80 hover:text-white py-2"
            >
              For Suppliers
            </Link>
            <Link 
              to="/buyers"
              onClick={() => setMobileMenuOpen(false)}
              className="text-white/80 hover:text-white py-2"
            >
              For Buyers
            </Link>
            <Link 
              to="/build-brand"
              onClick={() => setMobileMenuOpen(false)}
              className="text-white/80 hover:text-white py-2"
            >
              Build Brand
            </Link>
            <Link 
              to="/ai-blueprint"
              onClick={() => setMobileMenuOpen(false)}
              className="text-white/80 hover:text-white py-2"
            >
              AI Blueprint
            </Link>
            <Link 
              to="/trade-pay"
              onClick={() => setMobileMenuOpen(false)}
              className="text-white/80 hover:text-white py-2"
            >
              Trade Pay
            </Link>
            <Link 
              to="/distributor-reps"
              onClick={() => setMobileMenuOpen(false)}
              className="text-white/80 hover:text-white py-2"
            >
              Distributor Reps
            </Link>
            <Link 
              to="/target-list"
              onClick={() => setMobileMenuOpen(false)}
              className="text-white/80 hover:text-white py-2"
            >
              Target List
            </Link>
            <Link 
              to="/about"
              onClick={() => setMobileMenuOpen(false)}
              className="text-white/80 hover:text-white py-2"
            >
              About
            </Link>
            
            <div className="border-t border-white/10 pt-4 mt-2">
              {isAuthenticated ? (
                <>
                  <Link
                    to="/dashboard"
                    onClick={() => setMobileMenuOpen(false)}
                    className="inline-flex items-center justify-center gap-2 w-full rounded-xl bg-copper px-5 py-3 text-sm font-semibold text-white mb-3"
                  >
                    <User className="w-4 h-4" />
                    Dashboard
                  </Link>
                  <button
                    onClick={() => {
                      logout();
                      setMobileMenuOpen(false);
                    }}
                    className="w-full text-center text-white/70 hover:text-white py-2 text-sm"
                  >
                    Sign Out
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block text-center text-white/80 hover:text-white py-2 mb-2"
                  >
                    Sign In
                  </Link>
                  <Link
                    to="/register"
                    onClick={() => setMobileMenuOpen(false)}
                    className="inline-flex items-center justify-center w-full rounded-xl bg-copper px-5 py-3 text-sm font-semibold text-white"
                  >
                    Get Started
                  </Link>
                </>
              )}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
